import React, { useId, useState } from 'react'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { LEVELS, levelText, levelsFor } from './grant-words'
import { SegmentedChoice } from './segmented-choice'
import './api-keys.scss'

// The per-resource picker (contract §8; owner 2026-10-29, "won't scale for
// permissions": CLAUDE.md "API key form"), in the manner of GitHub's
// fine-grained token page: one row per resource group content offers here,
// hairlines between them. On the left the group's name (with the "EducationXR
// only" pill for EducationXR's own, platform_only) and its help in small muted
// text; on the right a segmented control of the levels in fixed, equal
// columns down the whole list (owner 2026-10-29: two-level rows started at
// another x and the list looked ragged): every row has No access | Read |
// Read and write in that order, and a level the group doesn't have
// (levelsFor; Courses is read-only, Package alerts has no Read) is a muted,
// disabled segment that says so on hover. A row granted
// above No access carries an accent edge and tint, so what a key holds can be
// scanned. Over the rows, "N of M granted" and Clear all (every group back to
// No access); past FILTER_THRESHOLD groups, a filter by name or description.
// When both kinds are offered the rows fall under "This organization" and
// "EducationXR platform"; with one kind, no headers. Narrow (a phone, a narrow
// drawer: a container query in api-keys.scss) the control goes under the text
// at full width, its columns still in step.
//
// These are a form's choices laid out as rows, so it is plain markup, not a
// data grid; each row's control is a real radio group named by the group.
// `value` is { groupID: level }; a choice hands back the whole grid with that
// group changed. `disabled` locks every choice and Clear all (a form that is
// saving: what is sent stays what is shown).

// More groups than this and the rows get a filter.
export const FILTER_THRESHOLD = 8

const TIERS = [
  { key: 'organization', title: 'This organization', platform: false },
  { key: 'platform', title: 'EducationXR platform', platform: true }
]

const matches = (group, needle) =>
  !needle || `${group.label || ''} ${group.description || ''}`.toLowerCase().includes(needle)

// Every level, in its column; the ones the group doesn't offer are unavailable.
const levelColumns = group => {
  const offered = levelsFor(group).map(choice => choice.value)
  return LEVELS.map(choice => {
    const unavailable = !offered.includes(choice.value)
    return {
      ...choice,
      grant: choice.value !== 'none',
      unavailable,
      title: unavailable ? `${group.label} has no ${levelText(choice.value).toLowerCase()} level` : undefined
    }
  })
}

const GrantRow = ({ group, level, rowID, disabled, onPick }) => {
  const granted = level !== 'none'
  const nameID = `${rowID}-name`
  const helpID = `${rowID}-help`
  return (
    <div className={`ak-grant-row${granted ? ' is-granted' : ''}`} data-testid={`grant-row-${group.id}`} data-granted={granted ? 'true' : 'false'}>
      <div className="ak-grant-text">
        <span className="ak-grant-name">
          <span id={nameID}>{group.label}</span>
          {group.platform_only && <Pill tone="info" title="Only a key of EducationXR's own organization can hold this, given by EducationXR staff.">EducationXR only</Pill>}
        </span>
        {group.description && <span className="ak-grant-help" id={helpID}>{group.description}</span>}
      </div>
      <SegmentedChoice name={rowID} labelledBy={nameID} describedBy={group.description ? helpID : undefined}
        options={levelColumns(group)} value={level} disabled={disabled} onChange={onPick} className="ak-seg-levels" />
    </div>
  )
}

export const GrantsGrid = ({ groups, value, onChange, labelledBy, disabled = false }) => {
  const prefix = useId()
  const [query, setQuery] = useState('')
  const all = groups || []
  const levelOf = group => (value && value[group.id]) || 'none'
  const granted = all.filter(group => levelOf(group) !== 'none').length
  const filterable = all.length > FILTER_THRESHOLD
  const needle = filterable ? query.trim().toLowerCase() : ''
  const shown = all.filter(group => matches(group, needle))
  const headed = all.some(group => group.platform_only) && all.some(group => !group.platform_only)
  const tiers = headed
    ? TIERS.map(tier => ({ ...tier, groups: shown.filter(group => !!group.platform_only === tier.platform) })).filter(tier => tier.groups.length > 0)
    : [{ key: 'all', groups: shown }]

  const pick = group => level => onChange({ ...(value || {}), [group.id]: level })
  const clearAll = () => onChange({ ...(value || {}), ...Object.fromEntries(all.map(group => [group.id, 'none'])) })

  return (
    <div className="ak-grants" role="group" aria-labelledby={labelledBy}>
      <div className="ak-grants-bar">
        <span className="ak-grants-count" aria-live="polite" data-testid="grants-count">{`${granted} of ${all.length} granted`}</span>
        <button type="button" className="btn btn-sm sw-btn ak-grants-clear" disabled={disabled || granted === 0} onClick={clearAll}>Clear all</button>
        {filterable &&
          <input type="search" className="form-control form-control-sm ak-grants-filter" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Filter by name or description" aria-label="Filter permissions" autoComplete="off" spellCheck={false} />}
      </div>
      {tiers.map(tier => tier.groups.length > 0 &&
        <div key={tier.key} className="ak-grants-tier" role={headed ? 'group' : undefined} aria-labelledby={headed ? `${prefix}-${tier.key}` : undefined}>
          {headed && <h4 className="ak-grants-tier-title" id={`${prefix}-${tier.key}`}>{tier.title}</h4>}
          <div className="ak-grant-rows">
            {tier.groups.map(group =>
              <GrantRow key={group.id} group={group} level={levelOf(group)} rowID={`${prefix}-${group.id}`} disabled={disabled} onPick={pick(group)} />)}
          </div>
        </div>)}
      {shown.length === 0 && all.length > 0 && <p className="sw-hint m-0 ak-grants-none">{`No permission matches “${query.trim()}”.`}</p>}
    </div>
  )
}
